import React, { useState } from 'react';
import { Card, Button, Typography, Row, Col, Space, List, message } from 'antd';
import { CheckOutlined, ArrowLeftOutlined, ThunderboltOutlined, RiseOutlined, StarOutlined } from '@ant-design/icons';
import { AppTheme } from '../../theme/themes';
import { logger } from '../../utils/logger';
import { APP_CONFIG } from '../../config/generated';
import { useTranslation } from 'react-i18next';

const { Title, Text, Paragraph } = Typography;

interface Plan {
  id: string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  icon: React.ReactNode;
  popular?: boolean;
  isElite?: boolean;
  priceId?: string; // Stripe Price ID
}

interface PlanSelectionViewProps {
  currentPlan: string;
  onSelectPlan: (planName: string) => void;
  onBack: () => void;
  theme: AppTheme;
  user?: any;
  onManageSubscription?: () => void;
}

const PlanSelectionView: React.FC<PlanSelectionViewProps> = ({
  currentPlan,
  onSelectPlan,
  onBack,
  theme,
  user,
  onManageSubscription
}) => {
  const { t } = useTranslation();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  // Load Price IDs from APP_CONFIG (injected via Infisical)
  const STRIPE_PRICE_ID_PRO = (APP_CONFIG as any).STRIPE_PRICE_ID_PRO || 'price_1QZxxxxxx'; 
  
  const plans: Plan[] = [
    {
      id: 'starter',
      name: 'Starter',
      price: '$0',
      period: '/mo',
      description: t('plans.starter.description'),
      features: [
        t('plans.starter.feature.standardIndicators'),
        t('plans.starter.feature.dailyTokens')
      ],
      icon: <ThunderboltOutlined style={{ fontSize: 24, color: theme.textSecondary }} />
    },
    {
      id: 'pro',
      name: 'Pro Trader',
      price: '$20',
      period: '/mo',
      description: t('plans.pro.description'),
      features: [
        t('plans.pro.feature.advancedIndicators'),
        t('plans.pro.feature.dailyTokens'),
        t('plans.pro.feature.prioritySupport')
      ],
      popular: true,
      icon: <RiseOutlined style={{ fontSize: 24, color: theme.accentPrimary }} />,
      priceId: STRIPE_PRICE_ID_PRO 
    },
    {
      id: 'elite',
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: t('plans.enterprise.description'),
      features: [
        t('plans.enterprise.feature.unlimitedAI'),
        t('plans.enterprise.feature.accountManager'),
        t('plans.enterprise.feature.apiAccess'),
        t('plans.enterprise.feature.customStrategy')
      ],
      isElite: true,
      icon: <StarOutlined style={{ fontSize: 24, color: '#fbbf24' }} />, // amber-400
      // No priceId for Enterprise, handled via contact
    }
  ];

  const handlePlanSelection = async (plan: Plan) => {
    // If it's the free plan, maybe just handle it locally or show message
    if (plan.id === 'starter') {
      message.info(t('plans.messages.alreadyStarter'));
      return;
    }

    // Handle Enterprise Contact
    if (plan.id === 'elite') {
      window.location.href = 'mailto:team@alphora-ai.com?subject=Enterprise Plan Inquiry';
      return;
    }

    if (!plan.priceId || plan.priceId === 'price_1QZxxxxxx') {
      message.error(t('plans.messages.priceIdNotConfigured'));
      logger.error('Missing Price ID for plan:', plan.name);
      return;
    }

    try {
      setLoadingPlan(plan.name);
      logger.log(`Initiating checkout for ${plan.name} (${plan.priceId})`);

      // Call the backend to create a checkout session
      const idToken = localStorage.getItem('cognito_id_token');
      if (!idToken) {
        message.error(t('plans.messages.mustLoginUpgrade'));
        setLoadingPlan(null);
        return;
      }

      // Determine base URL
      const mode = (APP_CONFIG as any).mode || 'dev';
      const envConfig = APP_CONFIG.ENV_CONFIGS[mode as keyof typeof APP_CONFIG.ENV_CONFIGS];
      const backendUrl = envConfig?.API ? envConfig.API.replace(/\/$/, '') : 'http://localhost:5050';
      const { getApiHeaders } = await import('../../utils/apiHeaders');
      
      const response = await fetch(`${backendUrl}/api/billing/checkout/session`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          price_id: plan.priceId,
          mode: 'subscription',
          success_url: window.location.origin + '/?billing_success=true',
          cancel_url: window.location.origin + '/?billing_cancel=true',
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('plans.messages.createCheckoutFailed'));
      }

      if (data.checkout_url) {
        logger.log('Redirecting to Stripe:', data.checkout_url);
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }

    } catch (error: any) {
      logger.error('Checkout error:', error);
      message.error(t('plans.messages.upgradeFailed', { error: error.message }));
      setLoadingPlan(null);
    }
  };

  return (
    <div className="animate-fade-in" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Button 
        type="text" 
        icon={<ArrowLeftOutlined />} 
        onClick={onBack}
        style={{ marginBottom: 24, color: theme.textSecondary }}
      >
        {t('plans.backToProfile')}
      </Button>

      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <Title level={1} style={{ color: theme.textPrimary, marginBottom: 12 }}>
          {t('plans.title')}
        </Title>
        <Paragraph style={{ color: theme.textSecondary, fontSize: 16 }}>
          {t('plans.subtitle')}
        </Paragraph>
      </div>

      <Row gutter={[24, 24]} justify="center">
        {plans.map((plan) => {
          const isCurrent = plan.name === currentPlan;
          const isElite = plan.isElite;
          const isLoading = loadingPlan === plan.name;

          // Prevent downgrading: map plan name to rank
          const rank = (name: string) => {
            if (name === 'Starter') return 0;
            if (name === 'Pro Trader') return 1;
            if (name === 'Enterprise') return 2;
            return 0;
          };
          const currentRank = rank(currentPlan);
          const thisRank = rank(plan.name);
          const isDowngrade = thisRank < currentRank;
          
          // Dynamic styles based on plan type and current selection
          const cardStyle: React.CSSProperties = {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 16,
            transition: 'all 0.3s ease',
            border: isCurrent 
              ? `2px solid ${theme.accentPrimary}` 
              : isElite 
                ? `1px solid ${theme.surfaceBorder}` 
                : `1px solid ${theme.surfaceBorder}`,
            background: isElite ? '#0f172a' : theme.surfaceBackground, // specific dark bg for elite if desired, or match theme
            boxShadow: isCurrent ? `0 0 20px ${theme.accentPrimary}40` : undefined,
            position: 'relative',
            overflow: 'hidden'
          };

          return (
            <Col xs={24} md={8} key={plan.id}>
              <Card 
                hoverable={!isCurrent}
                style={cardStyle}
                bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 32 }}
              >
                {plan.popular && (
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: theme.accentPrimary,
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 'bold',
                    padding: '4px 12px',
                    borderRadius: 20,
                    textTransform: 'uppercase'
                  }}>
                  {t('plans.mostPopular')}
                  </div>
                )}

                <div style={{ marginBottom: 24 }}>
                  <div style={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: 12, 
                    background: isElite ? 'rgba(255,255,255,0.1)' : theme.appBackground,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16
                  }}>
                    {plan.icon}
                  </div>
                  <Title level={3} style={{ color: isElite ? '#fff' : theme.textPrimary, marginBottom: 8, marginTop: 0 }}>
                    {plan.name}
                  </Title>
                  <Text style={{ color: isElite ? 'rgba(255,255,255,0.6)' : theme.textSecondary }}>
                    {plan.description}
                  </Text>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <span style={{ fontSize: 36, fontWeight: 'bold', color: isElite ? '#fff' : theme.textPrimary }}>
                    {plan.price}
                  </span>
                  <span style={{ color: isElite ? 'rgba(255,255,255,0.6)' : theme.textSecondary }}>
                    {plan.period}
                  </span>
                </div>

                <List
                  split={false}
                  dataSource={plan.features}
                  renderItem={item => (
                    <List.Item style={{ padding: '6px 0' }}>
                      <Space align="start">
                        <CheckOutlined style={{ color: isElite ? '#fbbf24' : theme.accentPrimary, marginTop: 4 }} />
                        <Text style={{ color: isElite ? 'rgba(255,255,255,0.8)' : theme.textSecondary }}>{item}</Text>
                      </Space>
                    </List.Item>
                  )}
                  style={{ flex: 1, marginBottom: 24 }}
                />

                <Button
                  size="large"
                  type={isCurrent ? 'default' : 'primary'}
                  onClick={() => !isCurrent && !isDowngrade && handlePlanSelection(plan)}
                  disabled={isCurrent || isLoading || isDowngrade}
                  loading={isLoading}
                  style={{
                    width: '100%',
                    height: 48,
                    borderRadius: 12,
                    fontWeight: 600,
                    background: isCurrent
                      ? theme.appBackground
                      : isElite
                        ? 'linear-gradient(to right, #fcd34d, #fbbf24)'
                        : undefined,
                    borderColor: isCurrent ? 'transparent' : undefined,
                    color: isElite ? '#451a03' : undefined,
                    cursor: isCurrent || isDowngrade ? 'not-allowed' : undefined
                  }}
                >
                  {isCurrent
                    ? t('plans.button.currentPlan')
                    : isDowngrade
                      ? t('plans.button.notAvailable')
                      : isElite
                        ? t('plans.button.contactSales')
                        : t('plans.button.selectPlan')}
                </Button>

                {/* Only show Manage subscription for paid (non-Starter) current plans */}
                {isCurrent && !isElite && plan.id !== 'starter' && onManageSubscription && (
                  <Button
                    type="link"
                    size="small"
                    onClick={onManageSubscription}
                    style={{ marginTop: 8, padding: 0 }}
                  >
                    {t('profile.plan.manageSubscription')}
                  </Button>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default PlanSelectionView;
